// SUP-349 — plain-JS port of vue-svgicon's rendering logic (svgicon.common.js:211-363, 3.2.8),
// with the install options gwbui used (`tagName: 'gwb-icon'`, `classPrefix: 'c-icon'`,
// `isStroke` unset => `fill` defaults true, `isOriginalDefault` unset => `original` defaults
// false). No Vue import — carries over to Vue 3 unchanged.
//
// Every quirk below is preserved verbatim for render parity (pinned by gwb-icon-parity.spec.js's
// GOLDEN fixture), not "fixed":
//   - iconViewBox's fallback for an unknown/undefined icon uses the `width` prop for BOTH
//     dimensions (the library's own `box()` reads `this.width` twice, never `this.height`).
//   - iconMarkup's title escape order is `<`, then `>`, then `&` — so a literal `<` in the title
//     ends up double-escaped as `&amp;lt;`.

const DIGIT_ONLY = /^\d+$/;
const SHAPE_OPEN_TAG = /<(path|rect|circle|polygon|line|polyline|ellipse)\s/gi;
const ORIGINAL_COLOR_ATTR = /_fill="|_stroke="/gi;
const DUPLICATE_FILL_STROKE = new RegExp(
  '<(path|rect|circle|polygon|line|polyline|ellipse)(\\sfill|\\sstroke)([="\\w\\s.\\-+#$&>]+)(fill|stroke)',
  'gi',
);

export function iconClass({ fill, dir }, classPrefix) {
  let clazz = `${classPrefix}-icon`;
  if (fill) clazz += ` ${classPrefix}-fill`;
  if (dir) clazz += ` ${classPrefix}-${dir}`;
  return clazz;
}

export function iconViewBox(iconData, width) {
  if (iconData) {
    if (iconData.viewBox) return iconData.viewBox;
    return `0 0 ${iconData.width} ${iconData.height}`;
  }
  // Ported quirk: the library's own box() computes `height` from `this.width`, never
  // `this.height`, for an icon with no iconData (unknown/undefined name).
  const w = width || 16;
  return `0 0 ${parseFloat(w)} ${parseFloat(w)}`;
}

export function iconStyle({ scale, width, height }, iconData) {
  const scaleNum = Number(scale);
  let w;
  let h;

  if (!Number.isNaN(scaleNum) && iconData) {
    w = `${Number(iconData.width) * scaleNum}px`;
    h = `${Number(iconData.height) * scaleNum}px`;
  } else {
    w = DIGIT_ONLY.test(width) ? `${width}px` : (width || '');
    h = DIGIT_ONLY.test(height) ? `${height}px` : (height || '');
  }

  const style = {};
  if (w) style.width = w;
  if (h) style.height = h;
  return style;
}

function setTitle(data, title) {
  if (!title) return data;
  const escaped = title.replace(/</gi, '&lt;').replace(/>/gi, '&gt;').replace(/&/g, '&amp;');
  return `<title>${escaped}</title>${data}`;
}

function addOriginalColor(data) {
  return data.replace(ORIGINAL_COLOR_ATTR, (styleName) => styleName.slice(1));
}

function addColor(data, colors, fill) {
  let i = 0;
  return data.replace(SHAPE_OPEN_TAG, (match) => {
    let color = colors[i] || colors[colors.length - 1];
    i += 1;
    let useFill = fill;

    if (color && color === '_') return match;

    if (color && color.indexOf('r-') === 0) {
      useFill = !useFill;
      color = color.substr(2);
    }

    const style = useFill ? 'fill' : 'stroke';
    const reverseStyle = useFill ? 'stroke' : 'fill';
    return `${match}${style}="${color}" ${reverseStyle}="none" `;
  });
}

function getValidPathData(data, original, colors) {
  if (original && colors.length > 0) {
    return data.replace(DUPLICATE_FILL_STROKE, '<$1$2$3_$4');
  }
  return data;
}

export function iconMarkup(iconData, {
  title, original, color, fill,
} = {}) {
  let data = '';
  const colors = color ? color.split(' ') : [];

  if (iconData) {
    data = iconData.data;
    data = setTitle(data, title);
    if (original) data = addOriginalColor(data);
    if (colors.length > 0) data = addColor(data, colors, fill);
  }

  return getValidPathData(data, original, colors);
}
